<script>
import PromiseDialogDto from '@/models/dialog/promiseDialogDto'
import baseMixin from '@/mixins/baseMixin'
import PromiseDialogEvent from '@/constants/dialog/PromiseDialogEvent'
import markdownToHtmlConverter from '@/utils/markdown-to-html-converter'
import StyledDialogHeader from '../layout/styled-dialog-header.vue'
import PromiseDialog from './promise-dialog.vue'

export default {
  name: 'SideDrawerDialog',
  components: { PromiseDialog, StyledDialogHeader },
  mixins: [baseMixin],
  model: {
    prop: 'show',
    event: 'showUpdated',
  },
  props: {
    show: { type: Boolean },
    /**
     * Prevents the dialog from being closed when clicking on the overlay
     */
    persistent: { type: Boolean },
    /**
     * Dialog title
     */
    title: { type: String, default: '' },
    description: { type: String, default: '' },
    loading: { type: Boolean, default: false },
    /**
     * Displays a confirmation dialog when attempting to close this dialog.
     * If null, no dialog will appear.
     */
    dialogOnClose: { type: PromiseDialogDto, default: () => null },
    /**
     * Hides the card actions
     */
    hideActions: { type: Boolean },
    /**
     * Disables padding around the dialog's body content
     */
    noGutters: { type: Boolean },
    /**
     * Changes the width of the dialog.
     * Relates to the xl breakpoint on v-col.
     */
    xl: {
      type: Number,
      default: 6,
      validator: function (value) {
        return value >= 0 && value <= 12
      },
    },
    /**
     * Changes the width of the dialog.
     * Relates to the lg breakpoint on v-col.
     */
    lg: {
      type: Number,
      default: 9,
      validator: function (value) {
        return value >= 0 && value <= 12
      },
    },
    /**
     * Changes the width of the dialog.
     * Relates to the md breakpoint on v-col.
     */
    md: {
      type: Number,
      default: 12,
      validator: function (value) {
        return value >= 0 && value <= 12
      },
    },
    /**
     * Changes the width of the dialog.
     * Relates to the sm breakpoint on v-col.
     */
    sm: {
      type: Number,
      default: 12,
      validator: function (value) {
        return value >= 0 && value <= 12
      },
    },
    /**
     * Changes the width of the dialog.
     * Relates to the xs breakpoint on v-col.
     */
    xs: {
      type: Number,
      default: 12,
      validator: function (value) {
        return value >= 0 && value <= 12
      },
    },
  },
  computed: {
    compTransition() {
      return this.isMediumOrLower
        ? 'slide-y-reverse-transition'
        : 'slide-x-reverse-transition'
    },

    compDescription() {
      if (!this.description) return ''
      return markdownToHtmlConverter(this.description)
    },
    compDialogColumns() {
      return { xl: this.xl, lg: this.lg, md: this.md, sm: this.sm, xs: this.xs }
    },
    compOverlayColumns() {
      return {
        xl: 12 - this.xl,
        lg: 12 - this.lg,
        md: 12 - this.md,
        sm: 12 - this.sm,
        xs: 12 - this.xs,
      }
    },
  },
  methods: {
    closeDialog() {
      this.$emit('closeDialog')
      this.$emit('showUpdated', false)
    },
    async close() {
      if (!this.dialogOnClose) return this.closeDialog()

      const result = await this.$refs.dialog.show(this.dialogOnClose)
      if (result === PromiseDialogEvent.OK) this.closeDialog()
    },
  },
}
</script>

<template>
  <div>
    <v-dialog
      data-testid="side-drawer-dialog"
      :value="show"
      :transition="compTransition"
      fullscreen
      hide-overlay
      persistent
      no-click-animation
      scrollable
    >
      <v-row no-gutters class="fill-height">
        <v-col
          cols="0"
          v-bind="compOverlayColumns"
          data-testid="side-drawer-dialog-overlay"
          @click="!persistent ? close() : null"
        ></v-col>
        <v-col cols="12" v-bind="compDialogColumns" class="fill-height">
          <v-card class="fill-height d-flex flex-column" tile>
            <slot name="header" :close="close">
              <StyledDialogHeader
                :description="compDescription"
                :loading="loading"
                :title="title"
                @close="close"
              />
            </slot>

            <v-card-text
              :class="[
                'flex-grow-1 fill-height overflow-auto',
                noGutters ? 'pa-0' : 'px-sm-8 px-xs-0 pt-4',
              ]"
            >
              <slot> </slot>
            </v-card-text>
            <v-card-actions
              v-if="!hideActions"
              class="px-sm-8 pb-sm-4 px-xs-4 pb-xs-4"
            >
              <slot name="actions" :close="close">
                <v-spacer />
                <v-btn
                  data-testid="side-drawer-dialog-close-btn"
                  :large="!isMediumOrLower"
                  text
                  @click="close"
                >
                  {{ $t('general.close') }}
                </v-btn>
              </slot>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-dialog>
    <v-overlay :value="show" z-index="10" />

    <PromiseDialog ref="dialog" />
  </div>
</template>
